// src/pages/IPAMControl.tsx
import React from "react";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  Space,
  Typography,
  Tabs,
  Table,
  Switch,
  Modal,
  message,
  Dropdown,
  ConfigProvider,
  Divider,
  Drawer,
  Grid,
  Alert,
  Tooltip,
} from "antd";
import {
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  EyeOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useDataProvider, useNotification } from "@refinedev/core";
import { usePGBroadcastRefresh } from "../realtime/usePGBroadcastRefresh";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

/**
 * Option A: global per-page "don't shrink into unreadable placeholders"
 * (single-file, minimal, no CSS file)
 */
const NON_SHRINK_STYLE = {
  minWidth: 260, // ~20+ chars
  flex: "0 0 auto",
} as const;

/**
 * UI knobs
 */
const UI = {
  PAGE_MIN_WIDTH_PX: 520,

  // Legacy timer refresh (kept, but default off; realtime is preferred)
  AUTO_REFRESH_MS: 0,

  PREFIXES_PAGE_SIZE: 50,
  IPS_PAGE_SIZE: 50,

  SCROLL_TO_TOP_ON_OPEN: true,

  DRAWER_DESKTOP_WIDTH_PX: 420,
  DRAWER_MOBILE_WIDTH: "100vw" as const,

  // Make Card body padding match the Table cell padding better
  BROWSE_CARD_BODY_PADDING_PX: 0,
  // Keep search width aligned with table vibe
  SEARCH_MAX_WIDTH_PX: 420,

  // Realtime topics for this page
  // REALTIME_TOPICS: ["ipam.prefixes", "ipam.ip_addresses"] as const,
  REALTIME_TOPICS: ["ipam.prefixes", "ipam.ip_addresses, ipam.vrfs, ipam.prefix_status_types, ipam.ip_status_types"] as const,
};

function useIsMobile() {
  const screens = useBreakpoint();
  return !screens.sm;
}

function useAutoRefresh(cb: () => void, ms: number) {
  React.useEffect(() => {
    if (!ms || ms <= 0) return;
    const id = setInterval(cb, ms);
    return () => clearInterval(id);
  }, [cb, ms]);
}

function scrollToTopLeft() {
  if (!UI.SCROLL_TO_TOP_ON_OPEN) return;

  const el = document.scrollingElement || document.documentElement;

  try {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  } catch {
    window.scrollTo(0, 0);
  }

  try {
    el.scrollTop = 0;
    el.scrollLeft = 0;
  } catch {}

  try {
    document.body.scrollTop = 0;
    document.body.scrollLeft = 0;
  } catch {}
}

function scrollOverlayBodyToTop() {
  try {
    const modalBodies = document.querySelectorAll(".ant-modal-body");
    modalBodies.forEach((b) => ((b as HTMLElement).scrollTop = 0));
  } catch {}

  try {
    const drawerBodies = document.querySelectorAll(".ant-drawer-body");
    drawerBodies.forEach((b) => ((b as HTMLElement).scrollTop = 0));
  } catch {}
}

function onOverlayOpened() {
  scrollToTopLeft();
  setTimeout(() => {
    scrollOverlayBodyToTop();
  }, 0);
}

function focusLater(ref: React.RefObject<any>, delayMs = 50) {
  setTimeout(() => {
    try {
      ref.current?.focus?.();
    } catch {}
  }, delayMs);
}

function toMap(rows: Array<{ id: number; status: string }>) {
  const m: Record<number, string> = {};
  rows.forEach((r) => (m[r.id] = r.status));
  return m;
}

function filterBySearch<T extends Record<string, any>>(
  rows: T[],
  search: string,
) {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    Object.values(r).some(
      (v) => v != null && String(v).toLowerCase().includes(q),
    ),
  );
}

const IPAM_SCHEMA_DEFAULT = "ipam";
const IPV4_EXAMPLE_VRF = "default";
const DEFAULT_SCHEMA = "ipam";

function useRpcCaller() {
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();

  return React.useCallback(
    async (
      schema: string | undefined,
      fn: string,
      payload: Record<string, any>,
    ) => {
      const apiUrl =
        typeof dataProvider.getApiUrl === "function"
          ? dataProvider.getApiUrl()
          : "";
      const url = `${apiUrl.replace(/\/$/, "")}/rpc/${fn}`;

      const effectiveSchema = schema || DEFAULT_SCHEMA;

      return dataProvider.custom?.({
        url,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept-Profile": effectiveSchema,
          "Content-Profile": effectiveSchema,
          Prefer: "return=representation",
        },
        data: payload,
      });
    },
    [dataProvider],
  );
}

const sectionStyle: React.CSSProperties = { marginBottom: 16 };
const fullWidth: React.CSSProperties = { width: "100%" };

function IPAMControl() {
  const isMobile = useIsMobile();

  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();
  const { open } = useNotification();
  const rpc = useRpcCaller();

  // ✅ Realtime coordination:
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [remoteDirty, setRemoteDirty] = React.useState(false);
  const [busyOverlayOpen, setBusyOverlayOpen] = React.useState(false); // drawers/modals open

  // core data
  const [vrfs, setVrfs] = React.useState<any[]>([]);
  const [prefixes, setPrefixes] = React.useState<any[]>([]);
  const [ipAddrs, setIpAddrs] = React.useState<any[]>([]);
  const [prefixStatuses, setPrefixStatuses] = React.useState<any[]>([]);
  const [ipStatuses, setIpStatuses] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);

  // selections
  const [vrfName, setVrfName] = React.useState<string>(IPV4_EXAMPLE_VRF);

  // IPv4 action form state
  const [containerPrefix, setContainerPrefix] = React.useState("");
  const [containerOwner, setContainerOwner] = React.useState("");
  const [containerDesc, setContainerDesc] = React.useState("");

  const [subnetParent, setSubnetParent] = React.useState("");
  const [subnetLength, setSubnetLength] = React.useState<number>();
  const [subnetOwner, setSubnetOwner] = React.useState("");
  const [subnetDesc, setSubnetDesc] = React.useState("");

  const [ipParent, setIpParent] = React.useState("");
  const [ipHostname, setIpHostname] = React.useState("");

  const [staticIp, setStaticIp] = React.useState("");
  const [staticIpParent, setStaticIpParent] = React.useState("");
  const [staticIpHostname, setStaticIpHostname] = React.useState("");
  const [staticIpDesc, setStaticIpDesc] = React.useState("");

  // IPv6 action form state
  const [ipv6ContainerPrefix, setIpv6ContainerPrefix] = React.useState("");
  const [ipv6ContainerOwner, setIpv6ContainerOwner] = React.useState("");
  const [ipv6ContainerDesc, setIpv6ContainerDesc] = React.useState("");

  const [ipv6Subnet, setIpv6Subnet] = React.useState("");
  const [ipv6SubnetParent, setIpv6SubnetParent] = React.useState("");
  const [ipv6SubnetOwner, setIpv6SubnetOwner] = React.useState("");
  const [ipv6SubnetDesc, setIpv6SubnetDesc] = React.useState("");

  const [ipv6StaticIp, setIpv6StaticIp] = React.useState("");
  const [ipv6StaticIpParent, setIpv6StaticIpParent] = React.useState("");
  const [ipv6StaticIpHostname, setIpv6StaticIpHostname] = React.useState("");
  const [ipv6StaticIpDesc, setIpv6StaticIpDesc] = React.useState("");

  // tabs
  const [tabKey, setTabKey] = React.useState<
    "ipv4" | "ipv6" | "prefixes" | "ips"
  >("prefixes");

  // browse
  const [prefixSearch, setPrefixSearch] = React.useState("");
  const [ipSearch, setIpSearch] = React.useState("");

  // table pagination (client-side)
  const [prefixPage, setPrefixPage] = React.useState(1);
  const [prefixPageSize, setPrefixPageSize] = React.useState(
    UI.PREFIXES_PAGE_SIZE,
  );
  const [ipPage, setIpPage] = React.useState(1);
  const [ipPageSize, setIpPageSize] = React.useState(UI.IPS_PAGE_SIZE);

  // drawer editing/view
  const [prefixDrawerOpen, setPrefixDrawerOpen] = React.useState(false);
  const [prefixDrawerMode, setPrefixDrawerMode] = React.useState<
    "view" | "edit"
  >("edit");
  const [activePrefix, setActivePrefix] = React.useState<any | null>(null);

  const [ipDrawerOpen, setIpDrawerOpen] = React.useState(false);
  const [ipDrawerMode, setIpDrawerMode] = React.useState<"view" | "edit">(
    "edit",
  );
  const [activeIp, setActiveIp] = React.useState<any | null>(null);

  // Keep "overlay open" state for realtime safety
  React.useEffect(() => {
    setBusyOverlayOpen(!!prefixDrawerOpen || !!ipDrawerOpen);
  }, [prefixDrawerOpen, ipDrawerOpen]);

  const reloadData = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    setLoading(true);
    try {
      const [vrfRes, prefRes, ipRes, prefStRes, ipStRes] = await Promise.all([
        dataProvider.getList({
          resource: "vrfs",
          pagination: { current: 1, pageSize: 200 },
          sorters: [{ field: "name", order: "asc" }],
          meta: { schema: IPAM_SCHEMA_DEFAULT },
        }),
        dataProvider.getList({
          resource: "prefixes",
          pagination: { current: 1, pageSize: 2000 },
          sorters: [{ field: "prefix", order: "asc" }],
          meta: { schema: IPAM_SCHEMA_DEFAULT },
        }),
        dataProvider.getList({
          resource: "ip_addresses",
          pagination: { current: 1, pageSize: 2000 },
          sorters: [{ field: "address", order: "asc" }],
          meta: { schema: IPAM_SCHEMA_DEFAULT },
        }),
        dataProvider.getList({
          resource: "prefix_status_types",
          pagination: { current: 1, pageSize: 200 },
          sorters: [{ field: "id", order: "asc" }],
          meta: { schema: IPAM_SCHEMA_DEFAULT },
        }),
        dataProvider.getList({
          resource: "ip_status_types",
          pagination: { current: 1, pageSize: 200 },
          sorters: [{ field: "id", order: "asc" }],
          meta: { schema: IPAM_SCHEMA_DEFAULT },
        }),
      ]);

      setVrfs(vrfRes.data);
      setPrefixes(prefRes.data);
      setIpAddrs(ipRes.data);
      setPrefixStatuses(prefStRes.data);
      setIpStatuses(ipStRes.data);

      if ((!vrfName || vrfName === IPV4_EXAMPLE_VRF) && vrfRes.data?.length) {
        setVrfName(vrfRes.data[0].name);
      }

      // DO NOT reset paging on every realtime refresh; it's annoying.
      // Keep existing page, but ensure it's still valid via derived "Safe" vars.
    } catch (err: any) {
      open?.({
        type: "error",
        message: err?.message || "Failed to load IPAM data",
        description: err?.body?.details || err?.body?.hint,
      });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [dataProvider, open, vrfName]);

  React.useEffect(() => {
    reloadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadData, refreshKey]);

  // Legacy timer refresh (optional)
  useAutoRefresh(reloadData, UI.AUTO_REFRESH_MS);

  // ✅ Realtime refresh: when something changes, auto-refresh unless an edit/view drawer is open
  usePGBroadcastRefresh({
    topics: [...UI.REALTIME_TOPICS],
    debounceMs: 200,
    enabled: true,
    onChange: () => {
      if (busyOverlayOpen) {
        setRemoteDirty(true);
      } else {
        setRefreshKey((k) => k + 1);
      }
    },
  });

  // maps
  const prefixStatusById = React.useMemo(
    () => toMap(prefixStatuses),
    [prefixStatuses],
  );
  const ipStatusById = React.useMemo(() => toMap(ipStatuses), [ipStatuses]);

  const prefixStatusIdByName = React.useMemo(() => {
    const m: Record<string, number> = {};
    prefixStatuses.forEach((s: any) => (m[s.status] = s.id));
    return m;
  }, [prefixStatuses]);

  const prefixCidrById = React.useMemo(() => {
    const m: Record<string | number, string> = {};
    prefixes.forEach((p: any) => {
      m[p.id] = p.prefix;
    });
    return m;
  }, [prefixes]);

  const currentVrfId = React.useMemo(() => {
    const row = vrfs.find((v) => v.name === vrfName);
    return row ? row.id : null;
  }, [vrfs, vrfName]);

  const filteredPrefixesAll = React.useMemo(() => {
    if (!currentVrfId) return [];
    return filterBySearch(
      prefixes.filter((p) => p.vrf_id === currentVrfId),
      prefixSearch,
    );
  }, [prefixes, currentVrfId, prefixSearch]);

  const filteredIpsAll = React.useMemo(() => {
    if (!currentVrfId) return [];
    return filterBySearch(
      ipAddrs.filter((p) => p.vrf_id === currentVrfId),
      ipSearch,
    );
  }, [ipAddrs, currentVrfId, ipSearch]);

  const prefixesTotal = filteredPrefixesAll.length;
  const prefixMaxPage = Math.max(1, Math.ceil(prefixesTotal / prefixPageSize));
  const prefixPageSafe = Math.min(prefixPage, prefixMaxPage);
  const filteredPrefixes = React.useMemo(() => {
    const start = (prefixPageSafe - 1) * prefixPageSize;
    return filteredPrefixesAll.slice(start, start + prefixPageSize);
  }, [filteredPrefixesAll, prefixPageSafe, prefixPageSize]);

  const ipsTotal = filteredIpsAll.length;
  const ipMaxPage = Math.max(1, Math.ceil(ipsTotal / ipPageSize));
  const ipPageSafe = Math.min(ipPage, ipMaxPage);
  const filteredIps = React.useMemo(() => {
    const start = (ipPageSafe - 1) * ipPageSize;
    return filteredIpsAll.slice(start, start + ipPageSize);
  }, [filteredIpsAll, ipPageSafe, ipPageSize]);

  const availableIpv4Containers = React.useMemo(() => {
    if (!currentVrfId) return [];
    const cid = prefixStatusIdByName["container"] ?? 2;
    return prefixes.filter(
      (p) =>
        p.vrf_id === currentVrfId &&
        p.status_id === cid &&
        !p.prefix.includes(":"),
    );
  }, [prefixes, currentVrfId, prefixStatusIdByName]);

  const availableIpv6Containers = React.useMemo(() => {
    if (!currentVrfId) return [];
    const cid = prefixStatusIdByName["container"] ?? 2;
    return prefixes.filter(
      (p) =>
        p.vrf_id === currentVrfId &&
        p.status_id === cid &&
        p.prefix.includes(":"),
    );
  }, [prefixes, currentVrfId, prefixStatusIdByName]);

  const notifySuccess = (msg: string) => {
    if (open) open({ type: "success", message: msg });
    else message.success(msg);
  };

  const notifyError = (err: any, fallback = "Action failed") => {
    const msg = err?.message || fallback;
    const details = err?.body?.details || err?.body?.hint;
    if (open) open({ type: "error", message: msg, description: details });
    else message.error(msg);
  };

  // helper: fetch VRF id by name
  const getVrfIdByName = React.useCallback(
    async (name: string) => {
      const res = await dataProvider.getList({
        resource: "vrfs",
        pagination: { current: 1, pageSize: 1 },
        filters: [{ field: "name", operator: "eq", value: name }],
        meta: { schema: IPAM_SCHEMA_DEFAULT },
      });
      return res.data?.[0]?.id ?? null;
    },
    [dataProvider],
  );

  // helper: fetch prefix id by cidr inside vrf
  const getPrefixIdByCidr = React.useCallback(
    async (vrfId: string, cidr: string) => {
      const res = await dataProvider.getList({
        resource: "prefixes",
        pagination: { current: 1, pageSize: 1 },
        filters: [
          { field: "vrf_id", operator: "eq", value: vrfId },
          { field: "prefix", operator: "eq", value: cidr },
        ],
        meta: { schema: IPAM_SCHEMA_DEFAULT },
      });
      return res.data?.[0]?.id ?? null;
    },
    [dataProvider],
  );

  // ---- actions ----
  const handleAddContainer = async () => {
    try {
      const vrfId = await getVrfIdByName(vrfName);
      if (!vrfId) throw new Error(`VRF '${vrfName}' not found`);
      await rpc(IPAM_SCHEMA_DEFAULT, "add_prefix", {
        p_vrf_id: vrfId,
        p_prefix: containerPrefix,
        p_status: "container",
        p_owner: containerOwner,
        p_desc: containerDesc,
      });
      notifySuccess("Container prefix added");
      setRemoteDirty(false);
      reloadData();
    } catch (err: any) {
      notifyError(err);
    }
  };

  const handleAllocateSubnet = async () => {
    try {
      const vrfId = await getVrfIdByName(vrfName);
      if (!vrfId) throw new Error(`VRF '${vrfName}' not found`);
      const res = await rpc(IPAM_SCHEMA_DEFAULT, "allocate_subnet_ipv4", {
        p_vrf_id: vrfId,
        p_within: subnetParent,
        p_new_len: Number(subnetLength),
        p_owner: subnetOwner,
        p_desc: subnetDesc,
        p_status: "allocated",
      });
      if ((res as any)?.error && (res as any).error.code === "PGRST202") {
        await rpc(IPAM_SCHEMA_DEFAULT, "allocate_subnet_ipv4", {
          vrf_id: vrfId,
          within: subnetParent,
          new_len: Number(subnetLength),
          owner: subnetOwner,
          desc: subnetDesc,
          status: "allocated",
        });
      }
      notifySuccess("Subnet allocated");
      setRemoteDirty(false);
      reloadData();
    } catch (err: any) {
      notifyError(err);
    }
  };

  const handleAllocateIp = async () => {
    try {
      const vrfId = await getVrfIdByName(vrfName);
      if (!vrfId) throw new Error(`VRF '${vrfName}' not found`);
      const parentId = await getPrefixIdByCidr(vrfId, ipParent);
      if (!parentId) throw new Error(`Parent prefix not found: ${ipParent}`);
      await rpc(IPAM_SCHEMA_DEFAULT, "allocate_ip", {
        p_vrf_id: vrfId,
        p_parent_prefix_id: parentId,
        p_hostname: ipHostname,
      });
      notifySuccess("IP allocated");
      setRemoteDirty(false);
      reloadData();
    } catch (err: any) {
      notifyError(err);
    }
  };

  const handleAddStaticIp = async () => {
    try {
      const vrfId = await getVrfIdByName(vrfName);
      if (!vrfId) throw new Error(`VRF '${vrfName}' not found`);
      const parentId = await getPrefixIdByCidr(vrfId, staticIpParent);
      if (!parentId)
        throw new Error(`Parent prefix not found: ${staticIpParent}`);
      await rpc(IPAM_SCHEMA_DEFAULT, "add_ip", {
        p_vrf_id: vrfId,
        p_address: staticIp,
        p_prefix_id: parentId,
        p_hostname: staticIpHostname,
        p_desc: staticIpDesc,
        p_status: "reserved",
        p_is_static: true,
      });
      notifySuccess("Static IP reserved");
      setRemoteDirty(false);
      reloadData();
    } catch (err: any) {
      notifyError(err);
    }
  };

  const handleAddIpv6Container = async () => {
    try {
      const vrfId = await getVrfIdByName(vrfName);
      if (!vrfId) throw new Error(`VRF '${vrfName}' not found`);
      await rpc(IPAM_SCHEMA_DEFAULT, "add_prefix", {
        p_vrf_id: vrfId,
        p_prefix: ipv6ContainerPrefix,
        p_status: "container",
        p_owner: ipv6ContainerOwner,
        p_desc: ipv6ContainerDesc,
      });
      notifySuccess("IPv6 container prefix added");
      setRemoteDirty(false);
      reloadData();
    } catch (err: any) {
      notifyError(err);
    }
  };

  const handleAddIpv6Subnet = async () => {
    try {
      const vrfId = await getVrfIdByName(vrfName);
      if (!vrfId) throw new Error(`VRF '${vrfName}' not found`);
      const parentId = await getPrefixIdByCidr(vrfId, ipv6SubnetParent);
      if (!parentId)
        throw new Error(`Parent v6 prefix not found: ${ipv6SubnetParent}`);
      await rpc(IPAM_SCHEMA_DEFAULT, "add_prefix", {
        p_vrf_id: vrfId,
        p_prefix: ipv6Subnet,
        p_status: "allocated",
        p_parent_id: parentId,
        p_owner: ipv6SubnetOwner,
        p_desc: ipv6SubnetDesc,
      });
      notifySuccess("IPv6 subnet added");
      setRemoteDirty(false);
      reloadData();
    } catch (err: any) {
      notifyError(err);
    }
  };

  const handleAddIpv6StaticIp = async () => {
    try {
      const vrfId = await getVrfIdByName(vrfName);
      if (!vrfId) throw new Error(`VRF '${vrfName}' not found`);
      const parentId = await getPrefixIdByCidr(vrfId, ipv6StaticIpParent);
      if (!parentId)
        throw new Error(`Parent v6 prefix not found: ${ipv6StaticIpParent}`);
      await rpc(IPAM_SCHEMA_DEFAULT, "add_ip", {
        p_vrf_id: vrfId,
        p_address: ipv6StaticIp,
        p_prefix_id: parentId,
        p_hostname: ipv6StaticIpHostname,
        p_desc: ipv6StaticIpDesc,
        p_status: "allocated",
        p_is_static: true,
      });
      notifySuccess("IPv6 static host added");
      setRemoteDirty(false);
      reloadData();
    } catch (err: any) {
      notifyError(err);
    }
  };

  const handleDeletePrefix = async (id: string) => {
    try {
      await dataProvider.deleteOne({
        resource: "prefixes",
        id,
        meta: { schema: IPAM_SCHEMA_DEFAULT },
      });
      notifySuccess("Prefix deleted");
      setRemoteDirty(false);
      reloadData();
    } catch (err: any) {
      notifyError(err);
    }
  };

  const handleDeleteIp = async (id: string) => {
    try {
      await dataProvider.deleteOne({
        resource: "ip_addresses",
        id,
        meta: { schema: IPAM_SCHEMA_DEFAULT },
      });
      notifySuccess("IP deleted");
      setRemoteDirty(false);
      reloadData();
    } catch (err: any) {
      notifyError(err);
    }
  };

  const openPrefixDrawer = (row: any, mode: "view" | "edit") => {
    onOverlayOpened();
    setActivePrefix(row);
    setPrefixDrawerMode(mode);
    setPrefixDrawerOpen(true);
    setRemoteDirty(false);
  };

  const openIpDrawer = (row: any, mode: "view" | "edit") => {
    onOverlayOpened();
    setActiveIp(row);
    setIpDrawerMode(mode);
    setIpDrawerOpen(true);
    setRemoteDirty(false);
  };

  // Left-align search + title (no Card "extra" on right)
  const PrefixesHeader = (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "center",
      }}
    >
      <Input
        allowClear
        placeholder="Search prefixes…"
        value={prefixSearch}
        onChange={(e) => {
          setPrefixSearch(e.target.value);
          setPrefixPage(1);
        }}
        style={{
          width: "100%",
          maxWidth: UI.SEARCH_MAX_WIDTH_PX,
          flex: "0 0 auto",
        }}
      />
      <Text strong style={{ whiteSpace: "nowrap" }}>
        Browse &amp; Edit Prefixes
      </Text>
    </div>
  );

  const IpsHeader = (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "center",
      }}
    >
      <Input
        allowClear
        placeholder="Search IPs…"
        value={ipSearch}
        onChange={(e) => {
          setIpSearch(e.target.value);
          setIpPage(1);
        }}
        style={{
          width: "100%",
          maxWidth: UI.SEARCH_MAX_WIDTH_PX,
          flex: "0 0 auto",
        }}
      />
      <Text strong style={{ whiteSpace: "nowrap" }}>
        Browse &amp; Edit IP Addresses
      </Text>
    </div>
  );

  return (
    <ConfigProvider
      theme={{
        components: {
          Input: { style: NON_SHRINK_STYLE as any },
          InputNumber: { style: NON_SHRINK_STYLE as any },
          Select: { style: NON_SHRINK_STYLE as any },
        },
      }}
    >
      <div style={{ padding: 16, minWidth: UI.PAGE_MIN_WIDTH_PX }}>
        {/* ✅ Remote update banner (when drawer open) */}
        {remoteDirty && busyOverlayOpen && (
          <Alert
            type="warning"
            showIcon
            message="IPAM data changed on the server while you were viewing/editing."
            description={
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={async () => {
                    setRemoteDirty(false);
                    await reloadData();
                  }}
                >
                  Refresh now
                </Button>
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => setRemoteDirty(false)}
                >
                  Dismiss
                </Button>
              </div>
            }
            style={{ marginBottom: 12 }}
          />
        )}

        {/* Header */}
        <Space style={{ marginBottom: 16 }} align="center" wrap>
          <Title level={4} style={{ margin: 0, whiteSpace: "nowrap" }}>
            IPAM Dashboard
          </Title>

          <Select
            value={vrfName || undefined}
            onChange={(v) => {
              setVrfName(v);
              setPrefixPage(1);
              setIpPage(1);
            }}
            loading={loading}
            placeholder="Select VRF"
            style={{ flex: "0 0 auto" }}
          >
            {vrfs.map((v) => (
              <Select.Option key={v.id} value={v.name}>
                {v.name}
              </Select.Option>
            ))}
          </Select>

          <Tooltip title="Reload">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => setRefreshKey((k) => k + 1)}
              loading={loading}
              style={{ flex: "0 0 auto" }}
            >
              Reload
            </Button>
          </Tooltip>

          {currentVrfId ? (
            <Text type="secondary" style={{ flex: "0 0 auto" }}>
              VRF ID: {String(currentVrfId)}
            </Text>
          ) : null}

          {/* ✅ Quick status indicator */}
          <Text type="secondary" style={{ flex: "0 0 auto" }}>
            {busyOverlayOpen
              ? remoteDirty
                ? "Live: paused (changes pending)"
                : "Live: paused (drawer open)"
              : "Live: on"}
          </Text>
        </Space>

        <Tabs
          activeKey={tabKey}
          onChange={(k) => setTabKey(k as any)}
          items={[
            {
              key: "ipv4",
              label: "IPv4 Actions",
              children: (
                <>
                  <Card title="Add Container (IPv4)" style={sectionStyle}>
                    <Space direction="vertical" style={fullWidth}>
                      <Input
                        value={containerPrefix || undefined}
                        onChange={(e) => setContainerPrefix(e.target.value)}
                        placeholder="Prefix (e.g. 10.0.0.0/16)"
                        style={fullWidth}
                      />
                      <Input
                        value={containerOwner}
                        onChange={(e) => setContainerOwner(e.target.value)}
                        placeholder="Owner"
                        style={fullWidth}
                      />
                      <Input
                        value={containerDesc}
                        onChange={(e) => setContainerDesc(e.target.value)}
                        placeholder="Description"
                        style={fullWidth}
                      />
                      <Button
                        type="primary"
                        onClick={handleAddContainer}
                        style={fullWidth}
                      >
                        Add Container
                      </Button>
                    </Space>
                  </Card>

                  <Card title="Allocate Subnet (IPv4)" style={sectionStyle}>
                    <Space direction="vertical" style={fullWidth}>
                      <Select
                        value={subnetParent || undefined}
                        onChange={setSubnetParent}
                        showSearch
                        placeholder="Parent prefix"
                        style={fullWidth}
                        options={availableIpv4Containers.map((p) => ({
                          label: p.prefix,
                          value: p.prefix,
                        }))}
                      />
                      <InputNumber
                        value={subnetLength}
                        onChange={(v) => setSubnetLength(v ?? 24)}
                        style={fullWidth}
                        placeholder="Size of network bits (e.g. 24)"
                      />
                      <Input
                        value={subnetOwner}
                        onChange={(e) => setSubnetOwner(e.target.value)}
                        placeholder="Owner"
                        style={fullWidth}
                      />
                      <Input
                        value={subnetDesc}
                        onChange={(e) => setSubnetDesc(e.target.value)}
                        placeholder="Description"
                        style={fullWidth}
                      />
                      <Button
                        type="primary"
                        onClick={handleAllocateSubnet}
                        style={fullWidth}
                      >
                        Allocate Subnet
                      </Button>
                    </Space>
                  </Card>

                  <Card title="Allocate Host IP (IPv4)" style={sectionStyle}>
                    <Space direction="vertical" style={fullWidth}>
                      <Select
                        value={ipParent || undefined}
                        onChange={setIpParent}
                        showSearch
                        placeholder="Parent prefix"
                        style={fullWidth}
                        options={availableIpv4Containers
                          .concat(
                            prefixes.filter(
                              (p) =>
                                p.vrf_id === currentVrfId &&
                                !p.prefix.includes(":") &&
                                p.status_id !==
                                  (prefixStatusIdByName["container"] ?? -1),
                            ),
                          )
                          .map((p) => ({ label: p.prefix, value: p.prefix }))}
                      />
                      <Input
                        value={ipHostname}
                        onChange={(e) => setIpHostname(e.target.value)}
                        placeholder="Hostname (e.g. foo or foo.example.com)"
                        style={fullWidth}
                      />
                      <Button
                        type="primary"
                        onClick={handleAllocateIp}
                        style={fullWidth}
                      >
                        Allocate IP
                      </Button>
                    </Space>
                  </Card>

                  <Card title="Reserve Static IP (IPv4)" style={sectionStyle}>
                    <Space direction="vertical" style={fullWidth}>
                      <Input
                        value={staticIp}
                        onChange={(e) => setStaticIp(e.target.value)}
                        placeholder="Address (e.g. 10.0.0.254/32)"
                        style={fullWidth}
                      />
                      <Select
                        value={staticIpParent || undefined}
                        onChange={setStaticIpParent}
                        showSearch
                        placeholder="Parent prefix"
                        style={fullWidth}
                        options={prefixes
                          .filter(
                            (p) =>
                              p.vrf_id === currentVrfId &&
                              !p.prefix.includes(":"),
                          )
                          .map((p) => ({ label: p.prefix, value: p.prefix }))}
                      />
                      <Input
                        value={staticIpHostname}
                        onChange={(e) => setStaticIpHostname(e.target.value)}
                        placeholder="Hostname"
                        style={fullWidth}
                      />
                      <Input
                        value={staticIpDesc}
                        onChange={(e) => setStaticIpDesc(e.target.value)}
                        placeholder="Description"
                        style={fullWidth}
                      />
                      <Button
                        type="primary"
                        onClick={handleAddStaticIp}
                        style={fullWidth}
                      >
                        Reserve Static IP
                      </Button>
                    </Space>
                  </Card>
                </>
              ),
            },
            {
              key: "ipv6",
              label: "IPv6 Actions",
              children: (
                <>
                  <Card title="Add Container (IPv6)" style={sectionStyle}>
                    <Space direction="vertical" style={fullWidth}>
                      <Input
                        value={ipv6ContainerPrefix}
                        onChange={(e) => setIpv6ContainerPrefix(e.target.value)}
                        placeholder="IPv6 prefix (e.g. 2001:db8:100::/48)"
                        style={fullWidth}
                      />
                      <Input
                        value={ipv6ContainerOwner}
                        onChange={(e) => setIpv6ContainerOwner(e.target.value)}
                        placeholder="Owner"
                        style={fullWidth}
                      />
                      <Input
                        value={ipv6ContainerDesc}
                        onChange={(e) => setIpv6ContainerDesc(e.target.value)}
                        placeholder="Description"
                        style={fullWidth}
                      />
                      <Button
                        type="primary"
                        onClick={handleAddIpv6Container}
                        style={fullWidth}
                      >
                        Add IPv6 Container
                      </Button>
                    </Space>
                  </Card>

                  <Card title="Add IPv6 Subnet" style={sectionStyle}>
                    <Space direction="vertical" style={fullWidth}>
                      <Input
                        value={ipv6Subnet}
                        onChange={(e) => setIpv6Subnet(e.target.value)}
                        placeholder="IPv6 subnet (e.g. 2001:db8:100::/64)"
                        style={fullWidth}
                      />
                      <Select
                        value={ipv6SubnetParent || undefined}
                        onChange={setIpv6SubnetParent}
                        showSearch
                        placeholder="Parent v6 container"
                        style={fullWidth}
                        options={availableIpv6Containers.map((p) => ({
                          label: p.prefix,
                          value: p.prefix,
                        }))}
                      />
                      <Input
                        value={ipv6SubnetOwner}
                        onChange={(e) => setIpv6SubnetOwner(e.target.value)}
                        placeholder="Owner"
                        style={fullWidth}
                      />
                      <Input
                        value={ipv6SubnetDesc}
                        onChange={(e) => setIpv6SubnetDesc(e.target.value)}
                        placeholder="Description"
                        style={fullWidth}
                      />
                      <Button
                        type="primary"
                        onClick={handleAddIpv6Subnet}
                        style={fullWidth}
                      >
                        Add IPv6 Subnet
                      </Button>
                    </Space>
                  </Card>

                  <Card title="Reserve Static IP (IPv6)" style={sectionStyle}>
                    <Space direction="vertical" style={fullWidth}>
                      <Input
                        value={ipv6StaticIp}
                        onChange={(e) => setIpv6StaticIp(e.target.value)}
                        placeholder="IPv6 host address (e.g. 2001:db8:100::10/128)"
                        style={fullWidth}
                      />
                      <Select
                        value={ipv6StaticIpParent || undefined}
                        onChange={setIpv6StaticIpParent}
                        showSearch
                        placeholder="Parent v6 prefix"
                        style={fullWidth}
                        options={prefixes
                          .filter(
                            (p) =>
                              p.vrf_id === currentVrfId &&
                              p.prefix.includes(":"),
                          )
                          .map((p) => ({ label: p.prefix, value: p.prefix }))}
                      />
                      <Input
                        value={ipv6StaticIpHostname}
                        onChange={(e) =>
                          setIpv6StaticIpHostname(e.target.value)
                        }
                        placeholder="Hostname"
                        style={fullWidth}
                      />
                      <Input
                        value={ipv6StaticIpDesc}
                        onChange={(e) => setIpv6StaticIpDesc(e.target.value)}
                        placeholder="Description"
                        style={fullWidth}
                      />
                      <Button
                        type="primary"
                        onClick={handleAddIpv6StaticIp}
                        style={fullWidth}
                      >
                        Add IPv6 Static Host
                      </Button>
                    </Space>
                  </Card>
                </>
              ),
            },
            {
              key: "prefixes",
              label: "Prefixes",
              children: (
                <Card
                  title={PrefixesHeader}
                  bodyStyle={{ padding: UI.BROWSE_CARD_BODY_PADDING_PX }}
                >
                  <Table
                    size="small"
                    rowKey="id"
                    dataSource={filteredPrefixes}
                    pagination={{
                      current: prefixPageSafe,
                      pageSize: prefixPageSize,
                      total: prefixesTotal,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "25", "50", "100", "200"],
                      onChange: (p, ps) => {
                        setPrefixPage(p);
                        setPrefixPageSize(ps);
                      },
                    }}
                    columns={[
                      {
                        title: "",
                        key: "actions",
                        width: 50,
                        align: "center",
                        render: (_: any, record: any) => (
                          <Dropdown
                            trigger={["click"]}
                            menu={{
                              items: [
                                {
                                  key: "view",
                                  label: "View",
                                  icon: <EyeOutlined />,
                                  onClick: () =>
                                    openPrefixDrawer(record, "view"),
                                },
                                {
                                  key: "edit",
                                  label: "Edit",
                                  icon: <EditOutlined />,
                                  onClick: () =>
                                    openPrefixDrawer(record, "edit"),
                                },
                                {
                                  key: "delete",
                                  label: "Delete",
                                  icon: <DeleteOutlined />,
                                  danger: true,
                                  onClick: () => {
                                    onOverlayOpened();
                                    Modal.confirm({
                                      title: "Delete prefix?",
                                      content: `Delete ${record.prefix}?`,
                                      okText: "Delete",
                                      okButtonProps: { danger: true },
                                      cancelText: "Cancel",
                                      onOk: async () =>
                                        handleDeletePrefix(record.id),
                                    });
                                  },
                                },
                              ],
                            }}
                          >
                            <Button
                              type="text"
                              size="small"
                              icon={<MoreOutlined />}
                            />
                          </Dropdown>
                        ),
                      },
                      { title: "Prefix", dataIndex: "prefix", width: 180 },
                      {
                        title: "Parent",
                        dataIndex: "parent_id",
                        width: 160,
                        render: (v: any) => prefixCidrById[v] ?? (v ? v : "-"),
                      },
                      {
                        title: "Status",
                        dataIndex: "status_id",
                        width: 120,
                        render: (v: number) => prefixStatusById[v] ?? v,
                      },
                      { title: "Owner", dataIndex: "owner", width: 160 },
                      {
                        title: "Description",
                        dataIndex: "description",
                        width: 260,
                      },
                    ]}
                  />
                </Card>
              ),
            },
            {
              key: "ips",
              label: "IP Addresses",
              children: (
                <Card
                  title={IpsHeader}
                  bodyStyle={{ padding: UI.BROWSE_CARD_BODY_PADDING_PX }}
                >
                  <Table
                    size="small"
                    rowKey="id"
                    dataSource={filteredIps}
                    pagination={{
                      current: ipPageSafe,
                      pageSize: ipPageSize,
                      total: ipsTotal,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "25", "50", "100", "200"],
                      onChange: (p, ps) => {
                        setIpPage(p);
                        setIpPageSize(ps);
                      },
                    }}
                    columns={[
                      {
                        title: "",
                        key: "actions",
                        width: 50,
                        align: "center",
                        render: (_: any, record: any) => (
                          <Dropdown
                            trigger={["click"]}
                            menu={{
                              items: [
                                {
                                  key: "view",
                                  label: "View",
                                  icon: <EyeOutlined />,
                                  onClick: () => openIpDrawer(record, "view"),
                                },
                                {
                                  key: "edit",
                                  label: "Edit",
                                  icon: <EditOutlined />,
                                  onClick: () => openIpDrawer(record, "edit"),
                                },
                                {
                                  key: "delete",
                                  label: "Delete",
                                  icon: <DeleteOutlined />,
                                  danger: true,
                                  onClick: () => {
                                    onOverlayOpened();
                                    Modal.confirm({
                                      title: "Delete IP?",
                                      content: `Delete ${record.address}?`,
                                      okText: "Delete",
                                      okButtonProps: { danger: true },
                                      cancelText: "Cancel",
                                      onOk: async () =>
                                        handleDeleteIp(record.id),
                                    });
                                  },
                                },
                              ],
                            }}
                          >
                            <Button
                              type="text"
                              size="small"
                              icon={<MoreOutlined />}
                            />
                          </Dropdown>
                        ),
                      },
                      { title: "Address", dataIndex: "address", width: 160 },
                      {
                        title: "Prefix",
                        dataIndex: "prefix_id",
                        width: 160,
                        render: (v: any) => prefixCidrById[v] ?? (v ? v : "-"),
                      },
                      {
                        title: "Hostname",
                        dataIndex: "hostname_fqdn",
                        width: 240,
                      },
                      {
                        title: "Status",
                        dataIndex: "status_id",
                        width: 120,
                        render: (v: number) => ipStatusById[v] ?? v,
                      },
                      {
                        title: "Static",
                        dataIndex: "is_static",
                        width: 80,
                        render: (v: boolean) => (v ? "Yes" : "No"),
                      },
                      {
                        title: "Description",
                        dataIndex: "description",
                        width: 260,
                      },
                    ]}
                  />
                </Card>
              ),
            },
          ]}
        />

        <Divider style={{ marginTop: 16, marginBottom: 16 }} />

        <EditPrefixDrawer
          open={prefixDrawerOpen}
          mode={prefixDrawerMode}
          prefix={activePrefix}
          isMobile={isMobile}
          prefixStatuses={prefixStatuses}
          onClose={() => {
            setPrefixDrawerOpen(false);
            setActivePrefix(null);
          }}
          onSaved={() => {
            setPrefixDrawerOpen(false);
            setActivePrefix(null);
            setRemoteDirty(false);
            reloadData();
          }}
        />

        <EditIpDrawer
          open={ipDrawerOpen}
          mode={ipDrawerMode}
          ip={activeIp}
          isMobile={isMobile}
          ipStatuses={ipStatuses}
          onClose={() => {
            setIpDrawerOpen(false);
            setActiveIp(null);
          }}
          onSaved={() => {
            setIpDrawerOpen(false);
            setActiveIp(null);
            setRemoteDirty(false);
            reloadData();
          }}
        />
      </div>
    </ConfigProvider>
  );
}

export { IPAMControl };
export default IPAMControl;

// --------------- Edit prefix drawer ----------------
function EditPrefixDrawer({
  open,
  mode,
  prefix,
  isMobile,
  onClose,
  prefixStatuses,
  onSaved,
}: {
  open: boolean;
  mode: "view" | "edit";
  prefix: any;
  isMobile: boolean;
  onClose: () => void;
  prefixStatuses: any[];
  onSaved: () => void;
}) {
  const firstRef = React.useRef<any>(null);

  const [form] = Form.useForm();
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();
  const { open: notify } = useNotification();

  const readOnly = mode === "view";
  const drawerWidth = isMobile
    ? UI.DRAWER_MOBILE_WIDTH
    : UI.DRAWER_DESKTOP_WIDTH_PX;

  React.useEffect(() => {
    if (prefix) {
      form.setFieldsValue({
        prefix: prefix.prefix,
        owner: prefix.owner,
        description: prefix.description,
        status_id: prefix.status_id,
      });
    } else {
      form.resetFields();
    }
  }, [prefix, form]);

  React.useEffect(() => {
    if (!open) return;
    onOverlayOpened();
    focusLater(firstRef, 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    const values = await form.validateFields();
    try {
      await dataProvider.update({
        resource: "prefixes",
        id: prefix.id,
        variables: {
          owner: values.owner ?? null,
          description: values.description ?? null,
          status_id: values.status_id,
        },
        meta: { schema: IPAM_SCHEMA_DEFAULT },
      });
      notify?.({ type: "success", message: "Prefix updated" });
      onSaved();
    } catch (err: any) {
      notify?.({
        type: "error",
        message: err?.message || "Failed to update prefix",
        description: err?.body?.details || err?.body?.hint,
      });
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="left"
      width={drawerWidth as any}
      destroyOnClose
      style={isMobile ? { maxWidth: "100vw" } : undefined}
      afterOpenChange={(opened) => {
        if (!opened) return;
        onOverlayOpened();
        focusLater(firstRef, 80);
      }}
      title={
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text
            strong
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {readOnly ? "View Prefix" : "Edit Prefix"}
          </Text>
          {prefix ? (
            <Text
              type="secondary"
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              #{prefix.id} • {prefix.prefix}
            </Text>
          ) : null}
        </div>
      }
      extra={
        readOnly ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" onClick={handleSave} disabled={!prefix}>
              Save
            </Button>
          </Space>
        )
      }
    >
      {!prefix ? (
        <Text type="secondary">No prefix selected.</Text>
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item label="Prefix" name="prefix">
            <Input ref={firstRef} autoFocus readOnly />
          </Form.Item>

          <Form.Item label="Status" name="status_id">
            <Select disabled={readOnly}>
              {prefixStatuses.map((s) => (
                <Select.Option key={s.id} value={s.id}>
                  {s.status}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Owner" name="owner">
            <Input readOnly={readOnly} />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} readOnly={readOnly} />
          </Form.Item>
        </Form>
      )}
    </Drawer>
  );
}

// --------------- Edit IP drawer ----------------
function EditIpDrawer({
  open,
  mode,
  ip,
  isMobile,
  onClose,
  ipStatuses,
  onSaved,
}: {
  open: boolean;
  mode: "view" | "edit";
  ip: any;
  isMobile: boolean;
  onClose: () => void;
  ipStatuses: any[];
  onSaved: () => void;
}) {
  const firstRef = React.useRef<any>(null);

  const [form] = Form.useForm();
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();
  const { open: notify } = useNotification();

  const readOnly = mode === "view";
  const drawerWidth = isMobile
    ? UI.DRAWER_MOBILE_WIDTH
    : UI.DRAWER_DESKTOP_WIDTH_PX;

  React.useEffect(() => {
    if (ip) {
      form.setFieldsValue({
        address: ip.address,
        hostname_fqdn: ip.hostname_fqdn,
        description: ip.description,
        is_static: ip.is_static,
        status_id: ip.status_id,
      });
    } else {
      form.resetFields();
    }
  }, [ip, form]);

  React.useEffect(() => {
    if (!open) return;
    onOverlayOpened();
    focusLater(firstRef, 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    const values = await form.validateFields();
    try {
      await dataProvider.update({
        resource: "ip_addresses",
        id: ip.id,
        variables: {
          hostname_fqdn: values.hostname_fqdn ?? null,
          description: values.description ?? null,
          is_static: !!values.is_static,
          status_id: values.status_id,
        },
        meta: { schema: IPAM_SCHEMA_DEFAULT },
      });
      notify?.({ type: "success", message: "IP updated" });
      onSaved();
    } catch (err: any) {
      notify?.({
        type: "error",
        message: err?.message || "Failed to update IP",
        description: err?.body?.details || err?.body?.hint,
      });
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="left"
      width={drawerWidth as any}
      destroyOnClose
      style={isMobile ? { maxWidth: "100vw" } : undefined}
      afterOpenChange={(opened) => {
        if (!opened) return;
        onOverlayOpened();
        focusLater(firstRef, 80);
      }}
      title={
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text
            strong
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {readOnly ? "View IP" : "Edit IP"}
          </Text>
          {ip ? (
            <Text
              type="secondary"
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              #{ip.id} • {ip.address}
            </Text>
          ) : null}
        </div>
      }
      extra={
        readOnly ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" onClick={handleSave} disabled={!ip}>
              Save
            </Button>
          </Space>
        )
      }
    >
      {!ip ? (
        <Text type="secondary">No IP selected.</Text>
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item label="Address" name="address">
            <Input ref={firstRef} autoFocus readOnly />
          </Form.Item>

          <Form.Item label="Hostname" name="hostname_fqdn">
            <Input readOnly={readOnly} />
          </Form.Item>

          <Form.Item label="Status" name="status_id">
            <Select disabled={readOnly}>
              {ipStatuses.map((s) => (
                <Select.Option key={s.id} value={s.id}>
                  {s.status}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Static reservation"
            name="is_static"
            valuePropName="checked"
          >
            <Switch disabled={readOnly} />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} readOnly={readOnly} />
          </Form.Item>
        </Form>
      )}
    </Drawer>
  );
}

